import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard, List, Key, ScrollText, LogOut, Zap, Bot, Link2, BookOpen,
  KeyRound, X, ShieldCheck, ShieldOff,
} from "lucide-react";
import { cn } from "../lib/utils";

const API = `${window.location.origin}/api`;

function PasswordModal({ onClose, token }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const handle = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    if (form.next.length < 6) { toast.error("En az 6 karakter olmalı"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Şifre değiştirildi");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata oluştu");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm animate-fade-in-up">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#DC2626]" />
            <h3 className="font-bold text-gray-900 uppercase text-sm"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Şifre Değiştir</h3>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          {[
            { key: "current", label: "Mevcut Şifre", placeholder: "••••••••" },
            { key: "next",    label: "Yeni Şifre",   placeholder: "En az 6 karakter" },
            { key: "confirm", label: "Yeni Şifre (Tekrar)", placeholder: "••••••••" },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">{label}</label>
              <input
                type="password" value={form[key]} onChange={handle(key)}
                placeholder={placeholder} required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]"
              />
            </div>
          ))}
          <button type="submit" disabled={saving}
            className="w-full py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50 mt-1">
            {saving ? "Kaydediliyor..." : "Şifreyi Değiştir"}
          </button>
        </form>
      </div>
    </div>
  );
}

function TwoFAModal({ onClose, token }) {
  const [status, setStatus]   = useState(null);   // null | "setup" | "confirm_disable"
  const [qrData, setQrData]   = useState(null);   // {qr_image, secret}
  const [code, setCode]       = useState("");
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/auth/2fa/status`, h).then(r => setEnabled(r.data.enabled));
  }, []); // eslint-disable-line

  const startSetup = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/2fa/setup`, h);
      setQrData(r.data);
      setStatus("setup");
    } catch { toast.error("QR kodu oluşturulamadı"); }
    finally { setLoading(false); }
  };

  const handleEnable = async e => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("6 haneli kod girin"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, { code }, h);
      toast.success("Google Authenticator etkinleştirildi!");
      setEnabled(true);
      setStatus(null);
      setCode("");
    } catch (err) { toast.error(err.response?.data?.detail || "Geçersiz kod"); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/2fa/disable`, h);
      toast.success("Google Authenticator devre dışı bırakıldı");
      setEnabled(false);
      setStatus(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm animate-fade-in-up">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#DC2626]" />
            <h3 className="font-bold text-gray-900 uppercase text-sm"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Google Authenticator</h3>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        <div className="p-5">
          {/* Status badge */}
          <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${enabled ? "bg-green-50 border border-green-200" : "bg-gray-50 border border-gray-200"}`}>
            {enabled
              ? <><ShieldCheck className="w-4 h-4 text-green-600" /><span className="text-sm font-semibold text-green-700">Aktif — 2 adımlı doğrulama açık</span></>
              : <><ShieldOff className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-600">Devre dışı</span></>
            }
          </div>

          {/* Setup flow */}
          {!status && !enabled && (
            <button onClick={startSetup} disabled={loading}
              className="w-full py-2.5 bg-[#111827] hover:bg-black text-white text-sm font-bold rounded-md transition-colors">
              {loading ? "Yükleniyor..." : "QR Kodu Oluştur ve Kur"}
            </button>
          )}

          {status === "setup" && qrData && (
            <form onSubmit={handleEnable} className="space-y-4">
              <div className="text-center">
                <img src={qrData.qr_image} alt="QR Code"
                  className="w-44 h-44 mx-auto border border-gray-200 rounded-lg" />
                <p className="text-xs text-gray-500 mt-2">
                  Google Authenticator'ı açın →<br />
                  + ekle → QR kodu tara
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded border text-center">
                <p className="text-xs text-gray-400 mb-0.5">Manuel giriş için secret:</p>
                <code className="text-xs font-mono text-gray-700 break-all">{qrData.secret}</code>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">
                  Uygulamadaki 6 haneli kodu girin
                </label>
                <input type="text" inputMode="numeric" maxLength={6}
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g,""))}
                  placeholder="000000" autoFocus
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-2xl font-mono text-center tracking-widest focus:outline-none focus:border-[#DC2626]"
                  data-testid="2fa-setup-code-input"
                />
              </div>
              <button type="submit" disabled={loading || code.length !== 6}
                className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-md disabled:opacity-50">
                {loading ? "Doğrulanıyor..." : "Doğrula ve Etkinleştir"}
              </button>
            </form>
          )}

          {/* Disable */}
          {enabled && !status && (
            <button onClick={() => setStatus("confirm_disable")}
              className="w-full py-2.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold rounded-md transition-colors">
              Devre Dışı Bırak
            </button>
          )}
          {status === "confirm_disable" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center">
                2FA devre dışı bırakılırsa şifre bilinen herkes girebilir. Emin misiniz?
              </p>
              <div className="flex gap-2">
                <button onClick={handleDisable} disabled={loading}
                  className="flex-1 py-2 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 disabled:opacity-50">
                  {loading ? "..." : "Devre Dışı Bırak"}
                </button>
                <button onClick={() => setStatus(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm rounded-md">
                  İptal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/fiyat-listeleri", icon: List, label: "Fiyat Listeleri", end: false },
  { to: "/model-esleştirme", icon: Link2, label: "Model Eşleştirme", end: false },
  { to: "/chatbot-icerik", icon: Bot, label: "Chatbot İçerik", end: false },
  { to: "/entegrasyon", icon: BookOpen, label: "Entegrasyon", end: false },
  { to: "/api-anahtarlari", icon: Key, label: "API Anahtarları", end: false },
  { to: "/loglar", icon: ScrollText, label: "API Logları", end: false },
];

export default function Layout({ children }) {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#F9FAFB] overflow-hidden">
      {/* Sidebar */}
      <aside
        className="w-60 flex flex-col shrink-0 select-none"
        style={{ background: "#111827" }}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#DC2626] rounded-md flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div
                className="text-white font-bold tracking-widest uppercase text-base leading-tight"
                style={{ fontFamily: "Barlow Condensed, sans-serif", letterSpacing: "0.1em" }}
              >
                UÇAR
              </div>
              <div className="text-gray-400 text-[10px] uppercase tracking-widest leading-none">
                Price API
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 group",
                  isActive
                    ? "bg-[#DC2626] text-white"
                    : "text-gray-400 hover:bg-white/10 hover:text-white"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn("w-4 h-4 shrink-0", isActive ? "text-white" : "text-gray-500 group-hover:text-white")}
                    strokeWidth={1.5}
                  />
                  <span
                    className="font-semibold uppercase tracking-wider text-xs"
                    style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "13px" }}
                  >
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.username}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <button onClick={() => setShowPasswordModal(true)}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                  Şifre
                </button>
                <span className="text-gray-700 text-xs">·</span>
                <button onClick={() => setShowTwoFAModal(true)}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> 2FA
                </button>
              </div>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout}
              className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              title="Çıkış Yap">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>

      {showPasswordModal && (
        <PasswordModal token={token} onClose={() => setShowPasswordModal(false)} />
      )}
      {showTwoFAModal && (
        <TwoFAModal token={token} onClose={() => setShowTwoFAModal(false)} />
      )}
    </div>
  );
}
